import type { CityConfig } from "../hawaii-city/types";

export const kapoleiConfig: CityConfig = {
  city: "Kapolei",
  state: "Hawaii",
  stateAbbr: "HI",
  slug: "kapolei",
  route: "/cities/kapolei",
  title: "Emergency & Expedited Passport Services in Kapolei, HI — Fast Processing | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Kapolei, HI. Fast passport renewal and urgent passport help for West Oahu families and travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Kapolei, HI",
  heroSubtitle: "Kapolei and West Oahu residents — get your passport fast without traveling to Honolulu. Start online, ship overnight, and we handle the rest. As fast as 24 hours.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Modern%20suburban%20neighborhood%20in%20Kapolei%20Hawaii%20with%20palm%20trees%20and%20distant%20ocean%20view%2C%20golden%20afternoon%20sunlight%2C%20green%20lawns%20and%20tropical%20landscaping%2C%20warm%20vibrant%20colors%2C%20professional%20travel%20photography%2C%20clean%20simple%20background%20with%20warm%20tones%20and%20blue%20sky%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=kapolei-hero-01&orientation=landscape",
  heroImageAlt: "Kapolei Hawaii suburban neighborhood with palm trees and ocean view",
  geoPosition: "21.3354;-158.0572",
  geoPlacename: "Kapolei, Hawaii",
  regionalAgency: {
    name: "Honolulu Passport Agency",
    address: "300 Ala Moana Blvd, Room 1-224, Honolulu, HI 96850",
    distance: "About 25 miles east in downtown Honolulu",
  },
  nearbyCitiesZips: [
    { name: "Kapolei", zips: "96707" },
    { name: "Ewa Beach", zips: "96706" },
    { name: "Waipahu", zips: "96797" },
    { name: "Pearl City", zips: "96782" },
    { name: "Mililani", zips: "96789" },
    { name: "Wahiawa", zips: "96786" },
    { name: "Aiea", zips: "96701" },
    { name: "Waimalu", zips: "96701" },
    { name: "Honolulu", zips: "96815" },
    { name: "Kailua", zips: "96734" },
    { name: "Kaneohe", zips: "96744" },
    { name: "Makakilo", zips: "96707" },
    { name: "Nanakuli", zips: "96792" },
    { name: "Waianae", zips: "96792" },
    { name: "Kalaeloa", zips: "96707" },
    { name: "Royal Kunia", zips: "96797" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from your Kapolei home or office in minutes." },
    { icon: "ri-truck-line", title: "Ship Your Documents", desc: "Use overnight shipping from Kapolei to send your documents directly to us." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed before submission to reduce errors and delays." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the regional agency — no public appointment needed." },
  ],
  testimonials: [
    {
      name: "Brandon S.",
      location: "Kapolei, HI",
      rating: 5,
      text: "I live in Kapolei and didn't want to drive all the way to Honolulu for a passport appointment. Fast Passport Center made it so easy — I started online and shipped from the Kapolei FedEx. Passport arrived in under a week.",
      service: "Passport Renewal",
    },
    {
      name: "Malia T.",
      location: "Ewa Beach, HI",
      rating: 5,
      text: "My daughter's passport expired 2 weeks before our family trip to Japan. We live in Ewa Beach near Kapolei and FPC walked us through every step. The passport came back with days to spare.",
      service: "Child Passport Renewal",
    },
    {
      name: "James K.",
      location: "Waipahu, HI",
      rating: 5,
      text: "Business trip to Manila came up last minute. I'm in Waipahu, so I shipped overnight and had my expedited passport in 5 business days. Way faster than driving to the Honolulu agency.",
      service: "Expedited Passport",
    },
  ],
  facilities: [
    {
      id: "kapolei-usps",
      name: "Kapolei Post Office",
      type: "USPS Passport Acceptance",
      address: "590 Farrington Hwy",
      city: "Kapolei",
      state: "HI",
      zip: "96707",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Downtown Kapolei",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Appointment required. Call ahead to confirm passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "ewa-beach-usps",
      name: "Ewa Beach Post Office",
      type: "USPS Passport Acceptance",
      address: "91-830 Pohakupuna Rd",
      city: "Ewa Beach",
      state: "HI",
      zip: "96706",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "About 5 miles east",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Ewa Beach area option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "waipahu-usps",
      name: "Waipahu Post Office",
      type: "USPS Passport Acceptance",
      address: "94-260 Farrington Hwy",
      city: "Waipahu",
      state: "HI",
      zip: "96797",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "About 8 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Waipahu area option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "pearl-city-usps",
      name: "Pearl City Post Office",
      type: "USPS Passport Acceptance",
      address: "550 Kamehameha Hwy",
      city: "Pearl City",
      state: "HI",
      zip: "96782",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "About 12 miles northeast",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Pearl City area option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in Kapolei, HI?",
      a: "Through Fast Passport Center, expedited processing can be as fast as 24 hours. Standard government processing through a Kapolei post office can take 2–6 months. For West Oahu residents, our courier service is the fastest option without traveling to Honolulu.",
    },
    {
      q: "Do I need to drive to Honolulu to use Fast Passport Center?",
      a: "No. Most Kapolei and West Oahu travelers start online and ship documents via overnight courier from Kapolei, Ewa Beach, or Waipahu. There is no local office visit required.",
    },
    {
      q: "Where is the closest passport acceptance facility to Kapolei?",
      a: "The Kapolei Post Office at 590 Farrington Hwy accepts passport applications by appointment. Ewa Beach Post Office and Waipahu Post Office are also nearby options. All require appointments for passport acceptance.",
    },
    {
      q: "What if I need a passport for a family trip from Kapolei?",
      a: "Family passports are one of our most common requests from Kapolei and West Oahu. We handle renewals, child passports, and new applications for families. Each family member gets personalized guidance.",
    },
    {
      q: "Can I get an expedited passport if I've never had one before?",
      a: "Yes, but first-time applicants require an in-person acceptance clerk visit. The Kapolei Post Office is the most convenient option for West Oahu residents. We guide you through exactly what to bring.",
    },
    {
      q: "How does shipping from Kapolei work?",
      a: "You can ship overnight using FedEx or UPS from Kapolei, Ewa Beach, or Waipahu. Most West Oahu residents use the FedEx location at Kapolei Commons or the UPS Store near Ewa Beach.",
    },
    {
      q: "Is Fast Passport Center an official government agency?",
      a: "No — Fast Passport Center is a private, approved and registered U.S. Department of State passport courier service. We work within the official passport system to provide expedited processing.",
    },
    {
      q: "What if my passport is lost or stolen in Kapolei?",
      a: "Lost or stolen passport replacements require Form DS-64 and additional documentation. We guide Kapolei residents through the exact process, including the acceptance clerk visit and expedited submission.",
    },
    {
      q: "Can military families at Schofield Barracks use this service?",
      a: "Absolutely. We frequently serve military families in the Kapolei, Wahiawa, and Schofield Barracks area. Military travel often has tight deadlines, and our expedited processing is designed for urgent situations.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Kapolei", address: "485 Kapolei Pkwy, Kapolei, HI 96707", hours: "Daily 7 AM – 11 PM" },
    { name: "Walgreens — Ewa Beach", address: "91-1111 Keaunui Dr, Ewa Beach, HI 96706", hours: "Daily 7 AM – 11 PM" },
    { name: "CVS Pharmacy — Waipahu", address: "94-709 Farrington Hwy, Waipahu, HI 96797", hours: "Daily 7 AM – 10 PM" },
    { name: "The UPS Store — Kapolei", address: "590 Farrington Hwy, Kapolei, HI 96707", hours: "Call for hours" },
    { name: "Walmart — Kapolei", address: "1030 Makai Rd, Kapolei, HI 96707", hours: "Call for hours" },
  ],
  dmvLocations: [
    {
      name: "Kapolei DMV — Kapolei Satellite City Hall",
      address: "1000 Uluohia St, Kapolei, HI 96707",
      hours: "Mon–Fri, 7:45 AM – 4:00 PM",
      note: "Driver licenses, REAL ID, state IDs. Appointment recommended.",
      featured: true,
    },
    {
      name: "Waipahu DMV — Dillingham Blvd",
      address: "94-300 Pupupani St, Waipahu, HI 96797",
      hours: "Mon–Fri, 7:45 AM – 4:00 PM",
      note: "Waipahu area option. Appointment recommended.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "HNL",
      name: "Daniel K. Inouye International Airport",
      distance: "About 25 miles east — Honolulu",
      address: "300 Rodgers Blvd, Honolulu, HI 96819",
      note: "Major international hub for Hawaii. Most Kapolei travelers fly from HNL for international trips.",
      flightUrl: "https://www.hawaii.gov/hnl/",
      flightLabel: "HNL Airport Website",
      featured: true,
    },
    {
      code: "OGG",
      name: "Kahului Airport (Maui)",
      distance: "Inter-island flights — Maui",
      address: "1 Keolani Pl, Kahului, HI 96732",
      note: "Inter-island connection option. Some Kapolei residents connect through Maui for international travel.",
      flightUrl: "https://www.hawaii.gov/ogg/",
      flightLabel: "OGG Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from Kapolei and West Oahu.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Kapolei travelers renew fast and avoid delays — no agency visit required for most renewals.",
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time applicants typically require an in-person acceptance clerk visit. We guide Kapolei residents through every step.",
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: "Replacements often require additional forms. We guide you through exactly what's needed to avoid costly mistakes.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Minors (15 and under) require parental participation and an in-person acceptance step. We provide clear instructions for Kapolei families.",
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage, divorce, or a legal name change — we assist with corrected passports for Kapolei residents.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-book-2-line",
      title: "Second Passport & Damaged",
      description: "Need a second passport for frequent travel, or replacing a damaged one? We handle both efficiently.",
      bullets: ["Frequent traveler solutions", "Damaged passport replacement", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Kapolei travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for Kapolei residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance for Kapolei residents with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for Kapolei residents with lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Kapolei, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service for Kapolei residents after marriage, divorce, or legal name changes." },
    { name: "Second Passport & Damaged", description: "Second passport issuance for frequent Kapolei travelers and damaged passport replacement." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Government processing times change constantly. Many Kapolei travelers assume 6–8 weeks is safe, only to find out it's now 3–4 months. Apply as soon as you book travel." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs so you get it right the first time." },
    { icon: "ri-file-damage-line", title: "Incomplete Forms", description: "One missing signature or wrong date and your application gets sent back. Our checklist catches errors before submission, saving you weeks of delay." },
    { icon: "ri-pen-nib-line", title: "Wrong Proof of Citizenship", description: "Not all birth certificates are accepted. Some require a long-form version. We tell you exactly which documents you need based on your situation." },
    { icon: "ri-calendar-close-line", title: "Missing Travel Proof for Emergency", description: "Regional agencies require proof of urgent travel. Without it, you're turned away. We help you prepare the right documentation before you need it." },
    { icon: "ri-map-pin-time-line", title: "Not Checking Name Consistency", description: "Your passport name must match your driver's license and ticket exactly. Middle names, suffixes, and hyphenated names cause more rejections than you'd think." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order. Takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist, instructions, and document review guidance." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using an overnight shipping option from Kapolei or Ewa Beach." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through our reserved passport agency availability — so you don't have to travel to Honolulu." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport is completed within the timeframe you selected. No guessing, no hoping an appointment opens up." },
  ],
  processingOptions: [
    { speed: "24", unit: "Hours*", label: "Regional Agency Processing", tag: "Emergency Only", tagColor: "bg-gray-100 text-gray-500", features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"], highlight: false, emergency: true },
    { speed: "2–9", unit: "Business Days", label: "Fast Passport Center", tag: "Most Popular", tagColor: "bg-[#F6C344] text-[#1A365D]", features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"], highlight: true },
    { speed: "3–5", unit: "Weeks", label: "Fast Passport Center", tag: "Standard Expedited", tagColor: "bg-[#1A365D]/10 text-[#1A365D]", features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"], highlight: false, smartSaver: true },
    { speed: "1–2", unit: "Months", label: "Self-Service (Post Office)", tag: "Slowest", tagColor: "bg-gray-100 text-gray-400", features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"], highlight: false, muted: true },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited availability, often weeks out", fpc: "No appointment needed to start", fpcGood: true },
    { feature: "In-Person Visit", agency: "Required — downtown Honolulu location", fpc: "Most travelers ship documents overnight from Kapolei", fpcGood: true },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options", fpcGood: true },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review to reduce delays", fpcGood: true },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission availability", fpcGood: true },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected", fpcGood: true },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only", fpc: "Most U.S. citizens with required documents", fpcGood: true },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Pearl City", route: "/cities/pearl-city" },
    { name: "Honolulu", route: "/cities/honolulu" },
    { name: "Kailua", route: "/cities/kailua" },
    { name: "Hilo", route: "/cities/hilo" },
    { name: "Mililani", route: "/cities/mililani" },
    { name: "Waipahu", route: "/cities/waipahu" },
  ],
  fpcOfficeAddress: "300 Ala Moana Blvd, Room 1-224, Honolulu, HI 96850",
  fpcOfficeNote: "After signing up online, choose what works best for you: courier pickup from your Kapolei home, ship with our prepaid label from any UPS/FedEx location in Ewa Beach or Waipahu, or schedule a drop-off. Most West Oahu clients ship overnight.",
  fpcOfficeDistance: "Serving Kapolei and all of West Oahu remotely",
  serviceAreaIntro: "Whether you are in Kapolei, Ewa Beach, Waipahu, or anywhere in West Oahu, Fast Passport Center makes expedited passport processing simple — no driving to Honolulu, no agency lines.",
  nearbyCitiesIntro: "Anywhere in West Oahu — start online, ship overnight from Kapolei, Ewa Beach, or Waipahu, and we handle the rest.",
  comparisonIntro: "",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Kapolei as simple as possible.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Kapolei travelers. Fast Passport Center reviews your documents before submission to catch these issues.",
  testimonialsIntro: "Real reviews from real travelers across Kapolei, Ewa Beach, and Waipahu who trusted Fast Passport Center with their expedited passports.",
  faqIntro: "Everything Kapolei and West Oahu travelers ask us most often about expedited passport services.",
  resourcesIntro: "",
  localTravelThemes: [],
  landmarks: [],
  island: "Oahu",
  islandNickname: "The Gathering Place",
  landmarkCards: [
    {
      name: "Ko Olina Lagoons",
      description: "Four man-made lagoons along Kapolei's western coastline, anchored by major resorts and Disney's Aulani. International visitors from around the world make Ko Olina a destination — and often discover passport needs on arrival.",
      imageUrl: "https://readdy.ai/api/search-image?query=Ko%20Olina%20lagoon%20Oahu%20Hawaii%20with%20calm%20turquoise%20water%2C%20white%20sand%20beach%2C%20tropical%20palm%20trees%2C%20resort%20area%20in%20background%2C%20beautiful%20sunny%20day%2C%20professional%20travel%20photography%2C%20luxury%20beach%20setting%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=kapolei-koolina&orientation=landscape",
    },
    {
      name: "Hawaii's Second City",
      description: "Kapolei has grown rapidly into Oahu's second urban center — home to government offices, shopping centers, and a growing business district serving all of West Oahu.",
      imageUrl: "https://readdy.ai/api/search-image?query=Modern%20suburban%20city%20Kapolei%20Hawaii%2C%20clean%20commercial%20district%20with%20palm%20trees%2C%20well-maintained%20roads%20and%20shopping%20center%2C%20warm%20tropical%20sunlight%2C%20professional%20urban%20photography%2C%20vibrant%20community%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=kapolei-city&orientation=landscape",
    },
    {
      name: "Ewa Beach Coastline",
      description: "The sandy shoreline between Kapolei and Ewa Beach offers a quieter alternative to crowded Waikiki. Families here frequently travel to Asia and the Pacific.",
      imageUrl: "https://readdy.ai/api/search-image?query=Ewa%20Beach%20Oahu%20Hawaii%20shoreline%2C%20peaceful%20sandy%20beach%20with%20gentle%20waves%2C%20turquoise%20ocean%2C%20palm%20trees%2C%20clear%20blue%20sky%2C%20warm%20afternoon%20light%2C%20professional%20travel%20photography%2C%20no%20crowds%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=kapolei-ewa&orientation=landscape",
    },
  ],
  travelTips: [
    { icon: "ri-flight-takeoff-line", title: "HNL Is 25 Miles East", description: "Kapolei travelers reach HNL via H-1. Budget 30–45 minutes and validate passport dates before booking. International fights from HNL serve Japan, Korea, Australia, and more." },
    { icon: "ri-hotel-line", title: "Ko Olina Resort Guests", description: "Tourists staying at Aulani, Four Seasons, and JW Marriott Ko Olina sometimes need urgent passport help. We can ship directly to resort addresses for visitors." },
    { icon: "ri-truck-line", title: "Ship from West Oahu", description: "FedEx at Kapolei Commons and UPS at Ewa Town Center offer overnight shipping for West Oahu residents. Ship by 6pm for next-morning delivery to our processing center." },
    { icon: "ri-shield-star-line", title: "Military Families at Barbers Point", description: "Coast Guard Air Station Barbers Point is nearby. We process passports for service members and dependents with military urgency consideration." },
    { icon: "ri-building-2-line", title: "Kapolei Government Center", description: "Kapolei's Satellite City Hall handles state IDs and REAL ID — useful if you need an updated government ID for your passport application." },
    { icon: "ri-global-line", title: "Growing Asian-Pacific Community", description: "West Oahu has a vibrant Filipino, Korean, and Japanese community with strong international travel patterns. Summer and holiday peaks mean plan passport renewal early." },
  ],
  statePageRoute: "/states/hawaii",
  statePageName: "Hawaii",
  dmvWebsite: "https://www.hawaii.gov/dot/highways/mvm",
  dmvLabel: "Hawaii DMV — Official Website",
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { label: "Birth & Death Certificates (HI DOH)", url: "https://health.hawaii.gov/vitalrecords/", desc: "Hawaii Department of Health — Office of Health Status Monitoring for birth, death, marriage, and divorce certificates statewide" },
    { label: "Marriage Certificates (HI DOH)", url: "https://health.hawaii.gov/vitalrecords/marriage-certificates/", desc: "Certified copies of Hawaii marriage certificates from the Department of Health Vital Records Office" },
    { label: "Divorce Decrees (HI Family Court)", url: "https://www.courts.state.hi.us/self-help/court-forms/court-forms-family-court", desc: "Hawaii State Judiciary Family Court — divorce decree records by circuit" },
    { label: "VitalChek Online Ordering", url: "https://www.vitalchek.com/", desc: "Order Hawaii birth, death, marriage, and divorce certificates online via VitalChek — fastest state-approved method" },
  ],
};